import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, MessageSquare, Repeat, AlertTriangle, BadgeCheck, Trash2, Loader2, Lock } from "lucide-react";
import { BadgeRow } from "@/components/Badges";
import { VerificationEdge } from "@/components/verify/VerificationEdge";
import { VouchMark } from "@/components/VouchMark";
import { timeAgo, magLabel, listingPath } from "@/lib/constants";
import { useCompare } from "@/context/CompareContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { openBuyerPass } from "@/components/BuyerPassModal";

const TYPE_LABEL = { sell: "FOR SALE", buy: "WANTED/ISO", trade: "FOR TRADE" };
const TYPE_COLOR = {
  sell: "text-volt border-volt/40",
  buy: "text-blaze border-blaze/40",
  trade: "text-foreground border-white/40",
};

// Never let an absurd price ($11T etc.) break the grid — compact-notate anything huge, and the
// element is also `truncate`d as a hard backstop.
const fmtPrice = (n) => {
  if (!(n > 0)) return "Contact";
  if (n >= 1e7) return `$${new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n)}`;
  return `$${n.toLocaleString()}`;
};

export default function ListingCard({ listing, index = 0 }) {
  const photo = (listing.photos || [])[0];
  const [imgError, setImgError] = useState(false);
  const isDealer = !!listing.seller_is_dealer;
  const roleText = isDealer ? "text-steel" : "text-olive-bright";
  const roleBorder = isDealer ? "border-steel/50" : "border-olive-bright/50";
  // Founding rank on the card: gold seller-line text + gold card border (no chip, no tint, no
  // banner). A revoked seller shows NO founding mark and NO gold border (revoke suppresses rank).
  const revoked = !!listing.seller_verification_revoked;
  const foundingNum = Number(listing.seller_founding_number);
  const isFounding = Number.isInteger(foundingNum) && foundingNum >= 1 && !revoked;
  const compare = useCompare();

  // BUYER-PASS GATE: server marks listing #6+ `gated` for a viewer without paid access, and
  // redacts price + seller. The card stays visible (photo protected, price/name blurred) and any
  // click opens the upgrade popup instead of navigating to the listing.
  const gated = !!listing.gated;

  // Owner quick-remove — free, instant, two-tap, straight from the card. Removing BEFORE a sale
  // never carries a penalty. Prevents the #1 cancellation cause: a listing left up after it sold
  // elsewhere.
  const { user } = useAuth();
  const isOwner = !!(user && listing.seller_id === user.user_id);
  const canRemove = isOwner && listing.status !== "sold" && listing.status !== "removed";
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removed, setRemoved] = useState(false);

  const doRemove = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setRemoving(true);
    try {
      await api.post(`/listings/${listing.listing_id}/status?status=removed`);
      toast.success("Listing removed — no penalty.");
      setRemoved(true);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't remove that — please try again.");
    } finally {
      setRemoving(false);
    }
  };

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (compare.has(listing.listing_id)) { compare.toggle(listing); return; }
    if (compare.atCap) { toast.error(`You can compare up to ${compare.cap} at once.`); return; }
    compare.toggle(listing);
  };

  // Inline structured-spec highlights (kept compact + neutral).
  const mag = magLabel(listing);
  const specBits = [
    listing.tube_diameter,
    mag,
    listing.focal_plane && listing.focal_plane !== "N/A" ? listing.focal_plane : null,
    listing.weight_oz != null ? `${listing.weight_oz}oz` : null,
  ].filter(Boolean);

  if (removed) return null;

  return (
    <Link
      to={gated ? "#" : listingPath(listing)}
      onClick={gated ? (e) => { e.preventDefault(); openBuyerPass(); } : undefined}
      data-testid={`listing-card-${listing.listing_id}`}
      data-gated={gated || undefined}
      className={`group block border bg-surface transition-colors animate-fade-up ${isFounding ? "" : "border-sniper hover:border-olive"} ${gated ? "cursor-pointer" : ""}`}
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms`, ...(isFounding ? { borderColor: "#7A6428" } : {}) }}
    >
      {/* verification edge — sits above the photo, inside the card border */}
      <VerificationEdge identityVerified={listing.seller_identity_verified} dealerVerified={listing.seller_dealer_verified} height={3} />
      {/* image — fit whole image (letterbox on the short axis), never center-crop; a used-optics
          buyer judges condition from this thumbnail, so we never cut off the subject's edges. */}
      <div className="relative aspect-square overflow-hidden" style={{ backgroundColor: "#111213" }}>
        {photo && !imgError ? (
          <img
            src={photo}
            alt={listing.title}
            onError={() => setImgError(true)}
            draggable={false}
            className={`w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 ${gated ? "select-none pointer-events-none" : ""}`}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center border border-dashed"
            style={{ backgroundColor: "#0D0E0F", borderColor: "#2E2F31" }}
            data-testid={`card-noimg-${listing.listing_id}`}>
            <span className="font-mono uppercase" style={{ fontSize: "10px", letterSpacing: ".1em", color: "#76797D" }}>
              {imgError ? "Image unavailable" : "No photo"}
            </span>
          </div>
        )}
        {/* Photo-PROTECTION overlay (gated only) — image stays visible, but right-click/drag-save is
            blocked and any tap opens the pass popup. NOT a blur (the photo is the hook). */}
        {gated && (
          <div
            className="absolute inset-0 z-10"
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            data-testid={`card-gate-overlay-${listing.listing_id}`}
          >
            <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wide bg-black/80 border border-volt/50 text-volt px-1.5 py-0.5">
              <Lock className="w-2.5 h-2.5" /> Buyer Pass
            </span>
          </div>
        )}
        <span
          className={`absolute top-2 left-2 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-black/80 border ${TYPE_COLOR[listing.listing_type] || TYPE_COLOR.sell}`}
        >
          {TYPE_LABEL[listing.listing_type] || "FOR SALE"}
        </span>
        {listing.boosted && (
          <span
            data-testid={`listing-boosted-${listing.listing_id}`}
            className="absolute top-2 right-2 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-black/70 border border-white/20 text-foreground/60"
          >
            Boosted
          </span>
        )}
        {compare && (
          <button
            onClick={handleCompare}
            data-testid={`compare-check-${listing.listing_id}`}
            data-active={compare.has(listing.listing_id) || undefined}
            title="Add to compare"
            className={`absolute ${listing.boosted ? "top-9" : "top-2"} right-2 inline-flex items-center gap-1 border px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide transition-colors ${
              compare.has(listing.listing_id)
                ? "bg-olive border-olive-bright text-white"
                : "bg-black/70 border-white/25 text-foreground/70 hover:border-olive-bright hover:text-foreground"
            }`}
          >
            <span className={`inline-block w-2.5 h-2.5 border ${compare.has(listing.listing_id) ? "bg-white border-white" : "border-white/50"}`} />
            Compare
          </button>
        )}
        {listing.flagged && (
          <span
            title="Flagged for review"
            className="absolute bottom-2 right-2 text-blaze bg-black/80 p-1 border border-blaze/40"
          >
            <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
          </span>
        )}
        {listing.itar && (
          <span className="absolute bottom-2 left-2 text-[9px] font-mono bg-blaze text-black px-1 py-0.5 uppercase">
            ITAR
          </span>
        )}
      </div>

      <div className="p-3">
        {/* account-type kicker — leads the card, color-coded by role */}
        <div className="flex items-center justify-between gap-2">
          <span
            data-testid={`card-kicker-${listing.listing_id}`}
            className={`font-mono text-[9px] font-semibold uppercase tracking-[0.16em] ${roleText}`}
          >
            {isDealer ? "Verified Dealer" : "Individual Seller"}
          </span>
          {listing.open_to_trades && (
            <span className="flex items-center gap-1 text-[10px] font-mono text-foreground/70 uppercase shrink-0">
              <Repeat className="w-3 h-3" /> Open to trade
            </span>
          )}
        </div>

        {/* title — large, above the price */}
        <h3 className="mt-1 font-heading text-lg font-bold leading-tight text-foreground line-clamp-2 min-h-[2.6rem]" data-testid={`card-title-${listing.listing_id}`}>
          {listing.title}
        </h3>

        {specBits.length > 0 && (
          <p className="mt-1 font-mono text-[10px] uppercase tracking-wide text-muted-foreground truncate" data-testid={`card-specs-${listing.listing_id}`}>
            {specBits.join(" · ")}
          </p>
        )}

        {/* price isolated in its own block above a divider; condition right-aligned */}
        <div className="mt-2.5 flex items-end justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[9px] uppercase tracking-wide text-muted-foreground">
              {listing.listing_type === "buy" ? "Looking for" : listing.has_variants ? "From" : "Asking"}
            </div>
            <div
              data-testid={`card-price-${listing.listing_id}`}
              className={`font-heading text-2xl font-bold leading-none truncate tabular-nums ${listing.listing_type === "buy" ? "text-foreground" : roleText}`}
            >
              {gated ? (
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Lock className="w-4 h-4 text-volt" strokeWidth={2.2} />
                  <span className="blur-[6px] select-none" aria-hidden>$••••</span>
                </span>
              ) : listing.listing_type === "buy" ? "ISO" : fmtPrice(listing.has_variants ? (listing.variant_price_min ?? listing.price) : listing.price)}
            </div>
          </div>
          {listing.listing_type !== "buy" && listing.has_variants ? (
            <span className="font-mono text-[10px] uppercase text-secondary-foreground text-right leading-tight max-w-[45%]" data-testid={`card-options-${listing.listing_id}`}>
              {(listing.variants || []).length} options
            </span>
          ) : listing.listing_type !== "buy" && listing.condition && (
            <span className="font-mono text-[10px] uppercase text-secondary-foreground text-right leading-tight max-w-[45%]" data-testid={`card-condition-${listing.listing_id}`}>
              {listing.condition}
            </span>
          )}
        </div>
        {listing.listing_type !== "buy" && listing.ship_mode === "own_label" && listing.shipping !== "pickup" && (
          <div className="mt-1 font-mono text-[9px] uppercase tracking-wide text-muted-foreground" data-testid={`card-shipping-${listing.listing_id}`}>
            {listing.free_shipping ? "Free shipping" : `+ $${((listing.flat_shipping_cents || 0) / 100).toFixed(0)} ship`}
          </div>
        )}

        <div className="mt-2.5 border-t border-sniper" />

        {/* seller identity — color + avatar shape by role */}
        <div className="mt-2 flex items-center text-[10px] font-mono">
          <div className="flex items-center gap-1.5 min-w-0">
          {listing.seller_avatar ? (
            <img
              src={listing.seller_avatar}
              alt=""
              className={`w-4 h-4 object-cover shrink-0 border ${roleBorder} ${isDealer ? "rounded-md" : "rounded-full"}`}
            />
          ) : (
            <span className={`w-4 h-4 shrink-0 border ${roleBorder} ${isDealer ? "rounded-md" : "rounded-full"} bg-obsidian`} />
          )}
          <span className={`truncate max-w-[80px] ${gated ? "text-muted-foreground blur-[4px] select-none" : roleText}`} aria-hidden={gated || undefined}>
            @{gated ? "collector" : listing.seller_username}
          </span>
          {isDealer && (
            <span className="inline-flex items-center gap-0.5 text-steel shrink-0" title="Verified dealer" data-testid={`card-dealer-${listing.listing_id}`}>
              <BadgeCheck className="w-3 h-3" strokeWidth={2} />
            </span>
          )}
          {isFounding && (
            <span data-testid={`card-founding-${listing.listing_id}`} className="shrink-0 whitespace-nowrap font-mono text-[10px]" style={{ color: "#E8C46A" }}>
              · FOUNDING #{foundingNum}
            </span>
          )}
          <BadgeRow badges={listing.seller_earned_badges} accountTone={listing.seller_account_tone} mode="card" />
          </div>
          {listing.seller_vouch_username && (
            <VouchMark inviter={listing.seller_vouch_username} size={13} testid={`card-vouch-${listing.listing_id}`} className="shrink-0" />
          )}
        </div>

        <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-muted-foreground uppercase">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {listing.city || "—"}{listing.state ? `, ${listing.state}` : ""}
          </span>
          <span className="flex items-center gap-2">
            <span className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3" /> {listing.message_count || 0}
            </span>
            <span>{timeAgo(listing.created_at)}</span>
          </span>
        </div>

        {canRemove && (
          <div className="mt-2 pt-2 border-t border-sniper" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
            {!confirming ? (
              <button
                type="button"
                data-testid={`card-remove-${listing.listing_id}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirming(true); }}
                className="w-full flex items-center justify-center gap-1.5 border border-sniper hover:border-blaze/60 text-muted-foreground hover:text-blaze font-mono uppercase text-[10px] tracking-wide py-1.5 transition-colors"
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            ) : (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  data-testid={`card-remove-confirm-${listing.listing_id}`}
                  onClick={doRemove}
                  disabled={removing}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-blaze/90 hover:bg-blaze text-black font-mono uppercase text-[10px] tracking-wide py-1.5 transition-colors disabled:opacity-50"
                >
                  {removing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Yes, remove
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirming(false); }}
                  className="px-2 py-1.5 border border-sniper text-muted-foreground hover:text-foreground font-mono uppercase text-[10px] tracking-wide transition-colors"
                >
                  Keep
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
